<template>
  <div class="contact-details__wrapper">
    <p class="contact-details__title">Contact details</p>
    <p class="contact-details__text">
      Please confirm who will be present for the whole duration of the
      installation. If this is someone other than yourself, please change the
      details below.
    </p>
    <div class="contact-details__container">
      <InputField
        v-model="contact.firstName"
        :has-error="v$.contact.firstName.$error"
        :error-message="firstNameErrorMessage"
        name="firstName"
        label="First Name"
      />
      <InputField
        v-model="contact.lastName"
        :has-error="v$.contact.lastName.$error"
        :error-message="lastNameErrorMessage"
        name="lastName"
        label="Last Name"
      />
      <InputField
        v-model="contact.telephone"
        :has-error="v$.contact.telephone.$error"
        :error-message="phoneErrorMessage"
        name="Phone"
        label="Phone Number"
      />
      <InputField
        v-model="contact.email"
        :has-error="v$.contact.email.$error"
        :error-message="emailErrorMessage"
        name="Email"
        label="Email"
      />
      <Checkbox
        v-model="isAdult"
        :has-error="v$.isAdult.$error"
        :error-message="isAdultErrorMessage"
        input-id="is-adult-confirmation"
        label="I confirm the person is aged 18+"
        data-testid="confirmation-checkbox"
      />
      <AppButton
        class="contact-details__next-button"
        data-testid="continue-button"
        @click="nextStep"
      >
        <span> Continue </span>
        <span class="contact-details__icon icon icon-arrow-right" />
      </AppButton>
    </div>
    <SaveContactNumberModal
      v-if="showSaveNumberModal"
      :show="showSaveNumberModal"
      :phone-number="contact.telephone"
      :customer-id="customerId"
      @done="onSaveNumberDone"
    />
  </div>
</template>

<script>
import Checkbox from "@soenergy/frontend-library/src/components/Checkbox.vue"
import AppButton from "@soenergy/frontend-library/src/components/AppButton.vue"
import InputField from "@soenergy/frontend-library/src/components/InputField.vue"
import { sessionStore } from "@soenergy/frontend-library/src/services/StorageFactory"
import SaveContactNumberModal from "@/components/SaveContactNumberModal.vue"
import useVuelidate from "@vuelidate/core"
import {
  email,
  maxLength,
  minLength,
  numeric,
  required,
} from "vuelidate/lib/validators"
import { mapActions, mapGetters, mapState } from "vuex"

const OFFERED_CONTACT_NUMBER_KEY = "offeredContactNumber"
export default {
  components: {
    InputField,
    Checkbox,
    AppButton,
    SaveContactNumberModal,
  },
  emits: ["go-forward"],
  setup() {
    return {
      v$: useVuelidate(),
    }
  },
  data() {
    return {
      contact: {},
      isAdult: false,
      showSaveNumberModal: false,
      hadNoNumberOnFile: false,
    }
  },
  mounted() {
    this.hadNoNumberOnFile = !this.primaryContact?.telephone
    this.getPrimaryContact()
    if (!this.contact.telephone) {
      this.v$.contact.telephone.$touch()
    }
  },
  computed: {
    ...mapState({
      form: (state) => state.homeDuringAppointment.form,
      customerId: (state) => state.junifer?.customer?.id,
    }),
    ...mapGetters({
      accountNumber: "accountNumber",
      primaryContact: "primaryContact",
    }),
    isFormSet() {
      return (
        this.form.firstName.length > 0 &&
        this.form.lastName.length > 0 &&
        this.form.telephone.length > 0 &&
        this.form.email.length > 0
      )
    },
    firstNameErrorMessage() {
      return this.v$.contact.firstName.$error ? "First Name is required" : false
    },
    lastNameErrorMessage() {
      return this.v$.contact.lastName.$error ? "Last Name is required" : false
    },
    phoneErrorMessage() {
      if (!this.v$.contact.telephone.$error) return false
      return this.contact.telephone
        ? "Please enter a valid phone number"
        : "We need a phone number so the engineer can contact you on the day"
    },
    emailErrorMessage() {
      return this.v$.contact.email.$error
        ? "Please enter a valid email address"
        : false
    },
    isAdultErrorMessage() {
      return this.v$.isAdult.$error
        ? "There must be an adult present for the installation to go ahead"
        : false
    },
  },

  methods: {
    ...mapActions({
      setForm: "setForm",
    }),
    getPrimaryContact() {
      if (this.isFormSet) {
        this.contact = this.form
        return
      }
      // A copy, so typing here doesn't write into the store getter's object
      this.contact = { ...this.primaryContact }
    },
    nextStep() {
      this.v$.$touch()
      if (this.v$.$invalid) return
      this.setForm(this.contact)
      if (this.shouldOfferToSaveNumber()) {
        this.showSaveNumberModal = true
        return
      }
      this.$emit("go-forward")
    },
    // A method, not computed: sessionStorage isn't reactive, so a cached
    // answer would go stale. Asked once per number, again if it changes.
    shouldOfferToSaveNumber() {
      return (
        this.hadNoNumberOnFile &&
        !!this.customerId &&
        sessionStore.getItem(OFFERED_CONTACT_NUMBER_KEY) !==
          this.contact.telephone
      )
    },
    onSaveNumberDone() {
      sessionStore.setItem(OFFERED_CONTACT_NUMBER_KEY, this.contact.telephone)
      this.showSaveNumberModal = false
      this.$emit("go-forward")
    },
  },

  validations: {
    contact: {
      firstName: {
        required,
      },
      lastName: {
        required,
      },
      telephone: {
        required,
        numeric,
        minLength: minLength(10),
        maxLength: maxLength(11),
      },
      email: {
        required,
        email,
      },
    },
    isAdult: {
      checked: (value) => value === true,
    },
  },
}
</script>

<style lang="scss" scoped>
.contact-details {
  font-size: $font-size-4;
  &__wrapper {
    margin-bottom: $space-12;
    ::v-deep .form-group {
      margin-bottom: $space-1;
    }
    ::v-deep .form-group__input-group {
      max-width: 100%;
    }
  }
  &__icon {
    margin-left: $space-1;
  }
  &__title {
    font-size: $font-size-5;
    font-weight: $weight-medium;
    margin-bottom: $space-2;

    @include md {
      font-size: $font-size-6;
    }
  }

  &__text {
    font-size: $font-size-3;
    margin-bottom: $space-8;

    @include md {
      font-size: $font-size-4;
    }
  }
  &__container {
    display: flex;
    flex-direction: column;
    gap: $space-1;

    &-text {
      font-size: $font-size-3;
    }
  }

  &__next-button {
    width: 100%;
    margin-top: $space-4;

    @include md {
      margin-bottom: $space-4;
    }
  }
}

::v-deep .checkbox__input:checked + .checkbox__label {
  font-weight: $weight-normal;
}
</style>
